import { Badge } from "@admin/components/atoms/Badge";
import { Button } from "@admin/components/atoms/Button";
import { Info } from "@admin/components/molecules/Person/Info";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import {
  CompanyLocation,
  dateToISO,
  getAllRoles,
  getHoursAndMinutes,
  getLocationUsers,
  getShiftsByDate,
  LocationUser,
  Shift,
  UserRole,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { add, endOfToday, startOfToday } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AddNewMultiPersonnelShiftModal,
  AddNewShiftModal,
  DeleteShiftModal,
  EditPlannedShiftModal,
  Hours,
  OpenShiftTable,
} from "../../components";

interface TomorrowShiftTablesProps {
  selectedLocation?: CompanyLocation;
  companyId: string;
}

export function TomorrowShiftsTables({
  selectedLocation,
  companyId,
}: TomorrowShiftTablesProps) {
  const { t } = useTranslation();
  const [isAddNewShiftModalOpen, setIsAddNewShiftModalOpen] = useState(false);
  const [
    isAddNewMultiPersonnelShiftModalOpen,
    setIsAddNewMultiPersonnelShiftModalOpen,
  ] = useState(false);
  const [selectedRowNoPlannedShift, setSelectedRowNoPlannedShift] =
    useState<LocationUser | null>(null);
  const [selectedRowPlannedShift, setSelectedRowPlannedShift] =
    useState<Shift>();
  const [isEditPlannedShiftModalOpen, setEditPlannedShiftModalOpen] =
    useState(false);
  const [isDeleteShiftModalOpen, setIsDeleteShiftModalOpen] = useState(false);

  const { data: locationUsers } = useQuery<LocationUser[]>({
    queryKey: ["tomorrowsShiftsLocationUsers", selectedLocation?.id],
    queryFn: () => getLocationUsers(selectedLocation?.id ?? ""),
    enabled: !!selectedLocation,
  });

  const startDate = add(startOfToday(), { days: 1 });
  const endDate = add(endOfToday(), { days: 1 });

  const { data: shifts, isLoading: shiftsLoading } = useQuery<Shift[]>({
    queryKey: ["shifts", selectedLocation?.id, startDate, endDate],
    queryFn: () =>
      getShiftsByDate({
        LocationId: selectedLocation?.id ?? "",
        StartDate: dateToISO(startDate),
        EndDate: dateToISO(endDate),
      }),
    enabled: !!selectedLocation,
  });

  const { data: roles, isLoading: rolesLoading } = useQuery<UserRole[]>({
    queryKey: ["personnelRoles"],
    queryFn: getAllRoles,
  });

  //tomorrow's shifts
  const tomorrowsShifts = useMemo(() => {
    return shifts?.filter(
      (shift) =>
        (shift.plannedStartDate &&
          new Date(shift.plannedStartDate) >= startDate &&
          new Date(shift.plannedStartDate) <= endDate) ||
        (shift.workStartDate &&
          new Date(shift.workStartDate) >= startDate &&
          new Date(shift.workStartDate) <= endDate),
    );
  }, [endDate, shifts, startDate]);

  const employeesWithNoShifts = useMemo(
    () =>
      locationUsers?.filter(
        (employee) =>
          !employee.isDeleted &&
          !tomorrowsShifts?.some((shift) => shift.userId === employee.userId),
      ),
    [locationUsers, tomorrowsShifts],
  );

  //open shifts
  const openShifts = useMemo(
    () =>
      tomorrowsShifts?.filter(
        (shift) =>
          shift.plannedStartDate &&
          !shift.workStartDate &&
          !shift.workEndDate &&
          !shift.userId,
      ) ?? [],

    [tomorrowsShifts],
  );

  // planned shifts
  const plannedShifts = useMemo(
    () =>
      tomorrowsShifts?.filter(
        (shift) =>
          shift.plannedStartDate &&
          !shift.workStartDate &&
          !shift.workEndDate &&
          !locationUsers?.find((e) => e.userId === shift.userId)?.isDeleted &&
          shift.userId,
      ) ?? [],
    [locationUsers, tomorrowsShifts],
  );

  const plannedShiftsColumns: DataTableColumn<Shift>[] = useMemo(
    () => [
      {
        header: t("common.Personnel"),
        accessorKey: "firstName",
        size: 225,
        cell: ({ row }) => (
          <Info
            userPhoto={row.original.userPhoto}
            name={row.original.firstName + " " + row.original.lastName}
            userName={row.original.userName}
          />
        ),
      },
      {
        header: t("shift.Teams"),
        accessorKey: "userRole",
        align: "center",
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex flex-row justify-center">
              <Badge type="success">{row.original.userRole}</Badge>
            </div>
          );
        },
      },
      {
        header: t("shift.Planned Times"),
        accessorKey: "plannedStartDate",
        align: "center",
        cell: ({ row }) => {
          return (
            <div className="flex flex-row justify-center ">
              <Hours>
                {getHoursAndMinutes(row.original.plannedStartDate)} -{" "}
                {getHoursAndMinutes(row.original.plannedEndDate)}
              </Hours>
            </div>
          );
        },
      },
      {
        id: "Actions",
        size: 100,
        cell: () => (
          <div className="flex flex-row justify-center gap-2.5">
            <i
              onClick={() => setEditPlannedShiftModalOpen(true)}
              className="ki-filled text-blue-400 ki-notepad-edit text-xl cursor-pointer"
            ></i>
            <i
              onClick={() => setIsDeleteShiftModalOpen(true)}
              className="ki-filled text-red-500 ki-trash-square text-xl cursor-pointer"
            ></i>
          </div>
        ),
      },
    ],
    [t],
  );

  const noPlannedShiftsColumns: DataTableColumn<LocationUser>[] = useMemo(
    () => [
      {
        header: t("common.Personnel"),
        accessorKey: "firstName",
        size: 225,
        cell: ({ row }) => (
          <Info
            userPhoto={row.original.userPhoto}
            name={row.original.firstName + " " + row.original.lastName}
            userName={row.original?.userName}
          />
        ),
      },
      {
        header: t("shift.Teams"),
        accessorKey: "userRole",
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex flex-row justify-center">
              <Badge type="success">
                {
                  roles?.find((r) => Number(r.id) === row.original.userRole)
                    ?.label
                }
              </Badge>
            </div>
          );
        },
        align: "center",
      },
      {
        id: "Actions",
        cell: () => (
          <div className="flex flex-row justify-center gap-2.5">
            <Button
              onClick={() => setIsAddNewShiftModalOpen(true)}
              variant="primary"
            >
              {t("shift.Plan a New Shift")}
            </Button>
          </div>
        ),
      },
    ],
    [roles, t],
  );

  return (
    <div className="flex flex-col gap-7.5">
      {plannedShifts.length > 0 && (
        <DataTable
          loading={shiftsLoading || rolesLoading}
          columns={plannedShiftsColumns}
          data={plannedShifts}
          title={t("shift.Personnel with Planned Shifts")}
          onRowClick={setSelectedRowPlannedShift}
        />
      )}

      {employeesWithNoShifts && employeesWithNoShifts.length > 0 && (
        <DataTable
          loading={shiftsLoading || rolesLoading}
          columns={noPlannedShiftsColumns}
          data={employeesWithNoShifts}
          onRowClick={setSelectedRowNoPlannedShift}
          title={t("shift.Personnel with No Planned Shifts")}
          header={
            <i
              onClick={() => setIsAddNewMultiPersonnelShiftModalOpen(true)}
              className="ki-duotone text-[#296573] ki-add-notepad text-2xl cursor-pointer"
            ></i>
          }
        />
      )}

      <OpenShiftTable
        selectedDate={startDate}
        title={t("shift.Tomorrow’s Open Shifts")}
        shifts={openShifts}
        companyId={companyId}
        selectedLocationId={selectedLocation?.id ?? ""}
      />

      <AddNewShiftModal
        open={isAddNewShiftModalOpen}
        personelId={selectedRowNoPlannedShift?.userId ?? ""}
        onClose={() => setIsAddNewShiftModalOpen(false)}
        selectedDate={startDate}
        locationId={selectedLocation?.id ?? ""}
        companyId={companyId}
        title={t("shift.Plan New Shift for Tomorrow With Name", {
          firstName: selectedRowNoPlannedShift?.firstName,
          lastName: selectedRowNoPlannedShift?.lastName,
        })}
      />

      <EditPlannedShiftModal
        open={isEditPlannedShiftModalOpen}
        onClose={() => setEditPlannedShiftModalOpen(false)}
        shift={selectedRowPlannedShift as Shift}
        companyLocationId={selectedLocation?.id ?? ""}
        companyId={companyId}
      />
      <AddNewMultiPersonnelShiftModal
        open={isAddNewMultiPersonnelShiftModalOpen}
        onClose={() => setIsAddNewMultiPersonnelShiftModalOpen(false)}
        selectedDate={startDate}
        locationId={selectedLocation?.id ?? ""}
        companyId={companyId}
        title={t("shift.Plan New Shift for Tomorrow")}
        locationUsers={employeesWithNoShifts ?? []}
      />
      <DeleteShiftModal
        open={isDeleteShiftModalOpen}
        onClose={() => setIsDeleteShiftModalOpen(false)}
        shift={selectedRowPlannedShift}
      />
    </div>
  );
}
